// @flow
import React, { Component } from "react"
import { rhythm } from "../utils/typography"
import { primaryDark } from "../utils/colors"
import Transition from "./Transition"
import TestimonialTile from "./TestimonialTile"
import QuoteBubble from "./QuoteBubble"
import centricityLogo from "../images/testimonials/testimonial-centricity-logo.png"
import nfpLogo from "../images/testimonials/testimonial-nfp-logo.png"
import podiumLogo from "../images/testimonials/testimonial-podium-logo.png"
import abisLogo from "../images/testimonials/testimonial-abis-logo.png"

type Props = {
  danielHeadshot: FixedImage,
  danHeadshot: FixedImage,
  willieHeadshot: FixedImage,
  aaronHeadshot: FixedImage,
}

type State = {
  activeIdx: number,
}

class Testimonials extends Component<Props, State> {
  setRotateInterval: () => setInterval
  rotateInterval: setInterval
  updateTestimonial: number => void
  rotateTestimonial: () => void
  defaultInterval: number
  testimonials: Array<Testimonial>

  constructor(props: Props) {
    super(props)
    this.defaultInterval = 5000
    this.testimonials = [
      {
        company: "Centricity",
        companyLogo: centricityLogo,
        backgroundColor: "#BF2E1A",
        quote:
          "Centricity has managed a wide variety of the benefit administration technologies on behalf of our clients. Each had its problems, so we were happy to finally find a bullet-proof solution in EaseCentral.",
        sourceImage: this.props.danielHeadshot,
        sourceName: "Daniel Lang",
        sourceTitle: "Centricity",
      },
      {
        company: "NFP",
        companyLogo: nfpLogo,
        backgroundColor: "#76B376",
        quote:
          "EaseCentral is a great system and should be a key partner for anyone administering employee benefits programs.",
        sourceImage: this.props.danHeadshot,
        sourceName: "Dan Garfin  ChFC, CLU",
        sourceTitle: "NFP, Director of Services, Key & Select Accounts",
      },
      {
        company: "Podium",
        companyLogo: podiumLogo,
        backgroundColor: "#9DC96B",
        quote:
          "I was at the point in my business where I was going to need to hire another support staff employee but my investment in EaseCentral has alleviated the need for another person on staff.",
        sourceImage: this.props.willieHeadshot,
        sourceName: "Willie Pinkston",
        sourceTitle: "Podium Benefits",
      },
      {
        company: "ABIS",
        companyLogo: abisLogo,
        backgroundColor: "#1C75BC",
        quote:
          "EaseCentral is an essential part of our business model, both for retaining our current clients and also for acquiring new ones.",
        sourceImage: this.props.aaronHeadshot,
        sourceName: "Aaron Bruski, Broker",
        sourceTitle: "ABIS Employee Benefits",
      },
    ]
  }

  state = {
    activeIdx: 0,
  }

  componentDidMount() {
    this.setRotateInterval()
  }

  componentWillUnmount() {
    clearInterval(this.rotateInterval)
  }

  setRotateInterval() {
    this.rotateInterval = setInterval(
      this.rotateTestimonial,
      this.defaultInterval
    )
  }

  rotateTestimonial = () => {
    const nextIdx =
      this.state.activeIdx >= this.testimonials.length - 1
        ? 0
        : ++this.state.activeIdx
    this.updateTestimonial(nextIdx)
  }

  updateTestimonial = (activeIdx: number) => {
    this.setState({ activeIdx })
    clearInterval(this.rotateInterval)
    this.setRotateInterval()
  }

  render() {
    return (
      <div css={styles.wrapper}>
        <h3 css={styles.headline}>
          Over 50,000 businesses enroll with EaseCentral.<br />
          Hear from their brokers:
        </h3>
        <div css={styles.gallery}>
          <div css={styles.tiles1}>
            <TestimonialTile
              idx={0}
              company={this.testimonials[0].company}
              companyLogo={this.testimonials[0].companyLogo}
              activeIdx={this.state.activeIdx}
              viewTestimonial={this.updateTestimonial}
            />
            <TestimonialTile
              idx={1}
              company={this.testimonials[1].company}
              companyLogo={this.testimonials[1].companyLogo}
              activeIdx={this.state.activeIdx}
              viewTestimonial={this.updateTestimonial}
            />
          </div>
          <div css={styles.tiles2}>
            <TestimonialTile
              idx={2}
              company={this.testimonials[2].company}
              companyLogo={this.testimonials[2].companyLogo}
              activeIdx={this.state.activeIdx}
              viewTestimonial={this.updateTestimonial}
            />
            <TestimonialTile
              idx={3}
              company={this.testimonials[3].company}
              companyLogo={this.testimonials[3].companyLogo}
              activeIdx={this.state.activeIdx}
              viewTestimonial={this.updateTestimonial}
            />
          </div>
          <div css={styles.featuredTestimonial}>
            <Transition current={this.state.activeIdx}>
              <QuoteBubble
                key={this.state.activeIdx}
                minHeight={rhythm(10)}
                css={styles.quoteBubble}
                {...this.testimonials[this.state.activeIdx]}
              />
            </Transition>
          </div>
        </div>
      </div>
    )
  }
}

const styles = {
  wrapper: {
    position: "relative",
    margin: `0 -${rhythm()}`,
    padding: `${rhythm(3)} ${rhythm(1 / 2)}`,
    "@media screen and (min-width: 940px)": {
      margin: "0",
    },
  },
  headline: {
    display: "block",
    color: primaryDark,
    marginBottom: rhythm(3),
    paddingLeft: rhythm(1 / 2),
    paddingRight: rhythm(1 / 2),
    "@media screen and (min-width: 620px)": {
      textAlign: "center",
    },
    "@media screen and (min-width: 940px)": {
      padding: "0",
    },
  },
  gallery: {
    display: "flex",
    flexWrap: "wrap",
  },
  tiles1: {
    display: "flex",
    order: "2",
    flex: "1 1 100%",
    position: "relative",
    zIndex: "10",
    "@media screen and (min-width: 500px)": {
      maxWidth: "50%",
    },
    "@media screen and (min-width: 940px)": {
      flexDirection: "column",
      order: "1",
      flex: "1",
    },
  },
  tiles2: {
    display: "flex",
    order: "3",
    flex: "1 1 100%",
    position: "relative",
    zIndex: "10",
    "@media screen and (min-width: 500px)": {
      maxWidth: "50%",
    },
    "@media screen and (min-width: 940px)": {
      flexDirection: "column",
      order: "3",
      flex: "1",
    },
  },
  featuredTestimonial: {
    order: "1",
    width: "100%",
    height: rhythm(18),
    position: "relative",
    zIndex: "1",
    "@media screen and (min-width: 940px)": {
      display: "block",
      order: "2",
      flex: "2",
      height: rhythm(20),
    },
  },
  quoteBubble: {
    padding: `0 ${rhythm(1 / 2)}`,
    maxWidth: rhythm(18),
    margin: "auto",
    "@media screen and (min-width: 940px)": {
      padding: rhythm(2),
      maxWidth: "100%",
    },
  },
}

export default Testimonials



// WEBPACK FOOTER //
// ./src/components/Testimonials.js